import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Fade,
  alpha,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Save,
  Delete,
  Add,
  Edit,
  Settings,
  Api,
  Rule,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';
import FormDialog from '../components/common/FormDialog';
import ConfirmDialog from '../components/common/ConfirmDialog';
import StatusBadge from '../components/common/StatusBadge';
import { PageLoader } from '../components/common/LoadingState';
import api from '../services/api';

const ConfigPage = () => {
  const [apiConfig, setApiConfig] = useState({
    externalApiUrl: '',
    externalApiKey: '',
    externalApiUser: '',
    externalApiPassword: '',
    batchSize: 10,
    maxSkusPerQuery: 100,
  });
  const [matchingRules, setMatchingRules] = useState([]);
  const [retails, setRetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiPassword, setShowApiPassword] = useState(false);

  // Dialog states
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [deleteRuleDialogOpen, setDeleteRuleDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [ruleFormData, setRuleFormData] = useState({
    name: '',
    retailId: '',
    feedKey: 'mpn',
    catalogKey: 'sku',
    normalization: ['trim', 'uppercase'],
    isDefault: false,
  });

  const fetchData = useCallback(async () => {
    try {
      const [configRes, rulesRes, retailsRes] = await Promise.all([
        api.get('/config/api'),
        api.get('/config/matching-rules'),
        api.get('/retails'),
      ]);
      setApiConfig(configRes.data);
      setMatchingRules(rulesRes.data);
      setRetails(retailsRes.data);
    } catch (err) {
      setError('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveApiConfig = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.put('/config/api', apiConfig);
      setSuccess('Configuración de API guardada exitosamente');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenRuleDialog = (rule = null) => {
    if (rule) {
      setRuleFormData({
        name: rule.name,
        retailId: rule.retailId,
        feedKey: rule.feedKey,
        catalogKey: rule.catalogKey,
        normalization: rule.normalization,
        isDefault: rule.isDefault,
      });
      setSelectedRule(rule);
    } else {
      setRuleFormData({
        name: '',
        retailId: '',
        feedKey: 'mpn',
        catalogKey: 'sku',
        normalization: ['trim', 'uppercase'],
        isDefault: false,
      });
      setSelectedRule(null);
    }
    setRuleDialogOpen(true);
  };

  const handleSaveRule = async () => {
    setSaving(true);
    setError('');

    try {
      if (selectedRule) {
        await api.put(`/config/matching-rules/${selectedRule.id}`, ruleFormData);
        setSuccess('Regla actualizada exitosamente');
      } else {
        await api.post('/config/matching-rules', ruleFormData);
        setSuccess('Regla creada exitosamente');
      }
      setRuleDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar regla');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async () => {
    try {
      await api.delete(`/config/matching-rules/${selectedRule.id}`);
      setDeleteRuleDialogOpen(false);
      setSelectedRule(null);
      setSuccess('Regla eliminada exitosamente');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar regla');
    }
  };

  const getRetailName = (retailId) => {
    const retail = retails.find(r => r.id === retailId);
    return retail?.name || '-';
  };

  const normalizationOptions = [
    { value: 'trim', label: 'Trim (quitar espacios)' },
    { value: 'uppercase', label: 'Mayúsculas' },
    { value: 'lowercase', label: 'Minúsculas' },
    { value: 'remove_spaces', label: 'Eliminar espacios' },
    { value: 'remove_dashes', label: 'Eliminar guiones' },
    { value: 'alphanumeric_only', label: 'Solo alfanuméricos' },
  ];

  if (loading) {
    return <PageLoader message="Cargando configuración..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Configuración"
        subtitle="Configura la API externa y reglas de matching"
        badge="Admin"
        badgeColor="secondary"
      />

      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        </Fade>
      )}

      {success && (
        <Fade in>
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Fade>
      )}

      <Grid container spacing={3}>
        {/* API Configuration */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                >
                  <Api />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    API Externa de Especificaciones
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Conexión con la API para obtener specs de productos
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="URL de la API"
                    value={apiConfig.externalApiUrl}
                    onChange={(e) => setApiConfig({ ...apiConfig, externalApiUrl: e.target.value })}
                    placeholder="https://api.example.com/products"
                    helperText="Endpoint de la API de especificaciones"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="API Key (opcional)"
                    type={showApiKey ? 'text' : 'password'}
                    value={apiConfig.externalApiKey}
                    onChange={(e) => setApiConfig({ ...apiConfig, externalApiKey: e.target.value })}
                    placeholder="••••••••••••••••"
                    helperText="Solo si la API usa autenticación por API Key"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowApiKey(!showApiKey)}
                            edge="end"
                            size="small"
                          >
                            {showApiKey ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Autenticación JWT (Usuario/Contraseña)
                    </Typography>
                  </Divider>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Usuario / Email"
                    value={apiConfig.externalApiUser}
                    onChange={(e) => setApiConfig({ ...apiConfig, externalApiUser: e.target.value })}
                    placeholder="usuario@ejemplo.com"
                    helperText="Usuario para autenticación JWT"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contraseña"
                    type={showApiPassword ? 'text' : 'password'}
                    value={apiConfig.externalApiPassword}
                    onChange={(e) => setApiConfig({ ...apiConfig, externalApiPassword: e.target.value })}
                    placeholder="••••••••••••••••"
                    helperText="Contraseña para autenticación JWT"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowApiPassword(!showApiPassword)}
                            edge="end"
                            size="small"
                          >
                            {showApiPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Tamaño de Batch"
                    type="number"
                    value={apiConfig.batchSize}
                    onChange={(e) => setApiConfig({ ...apiConfig, batchSize: parseInt(e.target.value) })}
                    inputProps={{ min: 1, max: 50 }}
                    helperText="SKUs por llamada a la API"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Máx SKUs por Query"
                    type="number"
                    value={apiConfig.maxSkusPerQuery}
                    onChange={(e) => setApiConfig({ ...apiConfig, maxSkusPerQuery: parseInt(e.target.value) })}
                    inputProps={{ min: 1, max: 500 }}
                    helperText="Límite por importación"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveApiConfig}
                      disabled={saving}
                      startIcon={<Save />}
                    >
                      Guardar Configuración
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Matching Rules */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                      color: 'secondary.main',
                    }}
                  >
                    <Rule />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Reglas de Matching
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cómo emparejar productos del feed con el catálogo
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => handleOpenRuleDialog()}
                >
                  Nueva
                </Button>
              </Box>

              {matchingRules.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    backgroundColor: (theme) => alpha(theme.palette.grey[100], 0.5),
                    borderRadius: 2,
                  }}
                >
                  <Typography color="text.secondary">
                    No hay reglas configuradas
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => handleOpenRuleDialog()}
                    sx={{ mt: 1 }}
                  >
                    Crear primera regla
                  </Button>
                </Box>
              ) : (
                <List disablePadding>
                  {matchingRules.map((rule, index) => (
                    <ListItem
                      key={rule.id}
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        mb: 1,
                        backgroundColor: (theme) => alpha(theme.palette.grey[50], 0.5),
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {rule.name}
                            </Typography>
                            {rule.isDefault && (
                              <Chip label="Default" size="small" color="primary" sx={{ height: 20 }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {getRetailName(rule.retailId)} • Feed: {rule.feedKey} → Catálogo: {rule.catalogKey}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Editar" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenRuleDialog(rule)}
                            sx={{ color: 'primary.main' }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedRule(rule);
                              setDeleteRuleDialogOpen(true);
                            }}
                            sx={{ color: 'error.main' }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Matching Rule Dialog */}
      <FormDialog
        open={ruleDialogOpen}
        title={selectedRule ? 'Editar Regla de Matching' : 'Nueva Regla de Matching'}
        subtitle="Define cómo emparejar productos del feed con el catálogo"
        icon={<Rule />}
        onClose={() => setRuleDialogOpen(false)}
        onSubmit={handleSaveRule}
        loading={saving}
      >
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nombre"
              value={ruleFormData.name}
              onChange={(e) => setRuleFormData({ ...ruleFormData, name: e.target.value })}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Retail</InputLabel>
              <Select
                value={ruleFormData.retailId}
                label="Retail"
                onChange={(e) => setRuleFormData({ ...ruleFormData, retailId: e.target.value })}
              >
                {retails.map((retail) => (
                  <MenuItem key={retail.id} value={retail.id}>
                    {retail.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Campo del Feed</InputLabel>
              <Select
                value={ruleFormData.feedKey}
                label="Campo del Feed"
                onChange={(e) => setRuleFormData({ ...ruleFormData, feedKey: e.target.value })}
              >
                <MenuItem value="mpn">MPN (g:mpn)</MenuItem>
                <MenuItem value="id">ID (g:id)</MenuItem>
                <MenuItem value="gtin">GTIN (g:gtin)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Campo del Catálogo</InputLabel>
              <Select
                value={ruleFormData.catalogKey}
                label="Campo del Catálogo"
                onChange={(e) => setRuleFormData({ ...ruleFormData, catalogKey: e.target.value })}
              >
                <MenuItem value="sku">SKU</MenuItem>
                <MenuItem value="prodnum">Prodnum</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Normalización</InputLabel>
              <Select
                multiple
                value={ruleFormData.normalization}
                label="Normalización"
                onChange={(e) => setRuleFormData({ ...ruleFormData, normalization: e.target.value })}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {normalizationOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </FormDialog>

      <ConfirmDialog
        open={deleteRuleDialogOpen}
        title="Eliminar Regla"
        message={`¿Estás seguro de que deseas eliminar la regla "${selectedRule?.name}"?`}
        type="delete"
        onConfirm={handleDeleteRule}
        onCancel={() => setDeleteRuleDialogOpen(false)}
      />
    </Box>
  );
};

export default ConfigPage;
